/**
 * ApisAssistant — proaktywny, kontekstowy asystent APIS.
 *
 * Pływa w prawym dolnym rogu panelu (StudioShell). Gdy user "stoi" na danym
 * ekranie, sam się odzywa: wita, pokazuje % gotowości profilu i NASTĘPNY krok
 * (regułowo, z /api/assistant/coach — hodowca i biznes). Pozwala też dopytać
 * o cokolwiek (czat → /api/assistant/ask).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, X, Send, Loader2, ArrowRight, Check, MessageCircle,
  Dog, Camera, FileText, Image as ImageIcon, Phone, Baby, Briefcase,
  ClipboardList, MapPin, Megaphone, MessageSquare, Bell, ArrowLeft, Inbox,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/utils/api';

const ICONS = {
  Dog, Camera, FileText, Image: ImageIcon, Phone, Baby, Briefcase,
  ClipboardList, MapPin, Megaphone, MessageSquare, Sparkles,
};

const pathInfo = (path) => {
  const isWizard = path.startsWith('/wizard/');
  let page = 'dashboard';
  if (path.startsWith('/panel/katalog')) page = 'katalog';
  else if (path.startsWith('/panel/leady')) page = 'leady';
  else if (path.startsWith('/panel/psy') || path.startsWith('/wizard/pies')) page = 'psy';
  else if (path.startsWith('/panel/reproduktory') || path.startsWith('/wizard/reproduktor')) page = 'reproduktory';
  else if (path.startsWith('/panel/mioty') || path.startsWith('/wizard/miot')) page = 'mioty';
  else if (path.includes('strona-firmy') || path.includes('firma-v2') || path.includes('/wizard/firma')) page = 'strona-firmy';
  else if (path.includes('strona-hodowli') || path.includes('hodowla-v2') || path.includes('/wizard/hodowla')) page = 'strona-hodowli';
  else if (path.includes('/oferta')) page = 'oferta';
  return { page, isWizard };
};

/* Polska odmiana: 1 zapytanie / 2-4 zapytania / 5+ zapytań */
const leadWord = (n) => {
  if (n === 1) return 'nieodebrane zapytanie';
  const d = n % 10, h = n % 100;
  if (d >= 2 && d <= 4 && !(h >= 12 && h <= 14)) return 'nieodebrane zapytania';
  return 'nieodebranych zapytań';
};

/* Pierścień postępu */
function Ring({ value }) {
  const r = 16, c = 2 * Math.PI * r;
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" className="-rotate-90 flex-shrink-0">
      <circle cx="20" cy="20" r={r} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="4" />
      <circle cx="20" cy="20" r={r} fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round"
        strokeDasharray={`${(value / 100) * c} ${c}`} className="transition-all duration-700" />
    </svg>
  );
}

/* Panel zgody + numeru do przypomnień WhatsApp (RODO: jasna informacja + zgoda + rezygnacja) */
function WaPanel({ onBack, headers }) {
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/reminders`, { headers });
      setPhone(res.data.phone || '');
      setEnabled(!!res.data.reminders_enabled);
    } catch { /* cicho */ } finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const enable = async () => {
    if (!phone.trim() || !consent || busy) return;
    setBusy(true); setStatus(null);
    try {
      const res = await axios.put(`${API}/whatsapp/reminders`, { phone: phone.trim(), enabled: true }, { headers });
      if (!res.data.ok) { setStatus({ type: 'err', msg: res.data.error || 'Nie udało się zapisać.' }); setBusy(false); return; }
      setEnabled(true);
      try {
        const snd = await axios.post(`${API}/whatsapp/reminders/send-now`, {}, { headers });
        setStatus({ type: 'ok', msg: snd.data?.ok ? 'Włączone! Wysłaliśmy pierwsze przypomnienie na WhatsApp.' : 'Włączone! Pierwsze przypomnienie dotrze wkrótce.' });
      } catch { setStatus({ type: 'ok', msg: 'Włączone! Pierwsze przypomnienie dotrze wkrótce.' }); }
    } catch (e) {
      setStatus({ type: 'err', msg: e?.response?.data?.error || 'Nie udało się zapisać numeru.' });
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true); setStatus(null);
    try {
      await axios.put(`${API}/whatsapp/reminders`, { phone: '', enabled: false }, { headers });
      setEnabled(false); setConsent(false);
      setStatus({ type: 'ok', msg: 'Przypomnienia wyłączone. Zgoda została wycofana.' });
    } catch { setStatus({ type: 'err', msg: 'Nie udało się.' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-3" data-testid="apis-assistant-wa">
      <button onClick={onBack} data-testid="apis-assistant-wa-back" className="inline-flex items-center gap-1 text-[12px] font-semibold text-[var(--apisai-text-secondary)] hover:text-[var(--apisai-text-primary)]">
        <ArrowLeft className="w-3.5 h-3.5" /> Wróć
      </button>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center"><Bell className="w-4 h-4 text-emerald-600" /></div>
        <p className="text-[14px] font-bold text-[var(--apisai-text-primary)]">Przypomnienia na WhatsApp</p>
      </div>
      <p className="text-[12px] text-[var(--apisai-text-secondary)] leading-relaxed">
        Wyślemy Ci krótkie przypomnienie na WhatsApp o brakujących krokach w profilu — maksymalnie 1 wiadomość co 3 dni. Zgodę możesz wycofać w każdej chwili (tutaj lub wpisując STOP w odpowiedzi).
      </p>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[var(--apisai-primary)]" /></div>
      ) : enabled ? (
        <div className="space-y-3">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2">
            <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-emerald-800">Przypomnienia włączone{phone ? ` na numer ${phone}` : ''}.</p>
          </div>
          <button onClick={disable} disabled={busy} data-testid="apis-assistant-wa-disable"
            className="w-full h-9 rounded-lg border border-[var(--apisai-border)] text-[var(--apisai-text-secondary)] text-[12px] font-semibold hover:bg-[var(--apisai-bg-subtle)] disabled:opacity-50">
            {busy ? 'Zapisywanie…' : 'Wyłącz przypomnienia'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+48 600 100 200"
            className="w-full text-[13px] px-3 h-10 rounded-lg border border-[var(--apisai-border)] focus:border-[var(--apisai-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--apisai-primary)]/20"
            data-testid="apis-assistant-wa-phone" />
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[var(--apisai-primary)]" data-testid="apis-assistant-wa-consent" />
            <span className="text-[11px] text-[var(--apisai-text-secondary)] leading-snug">
              Wyrażam zgodę na otrzymywanie przypomnień na WhatsApp od APISAI. Wiem, że mogę zrezygnować w każdej chwili.
            </span>
          </label>
          <button onClick={enable} disabled={!phone.trim() || !consent || busy} data-testid="apis-assistant-wa-enable"
            className="w-full h-9 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold hover:brightness-95 disabled:opacity-40 transition-all">
            {busy ? 'Zapisywanie…' : 'Włącz przypomnienia'}
          </button>
        </div>
      )}

      {status && (
        <p className={`text-[12px] ${status.type === 'ok' ? 'text-emerald-700' : 'text-red-600'}`} data-testid="apis-assistant-wa-status">{status.msg}</p>
      )}
    </div>
  );
}

export default function ApisAssistant() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [coach, setCoach] = useState(null);
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [agentResp, setAgentResp] = useState(null);
  const [asking, setAsking] = useState(false);
  const [waView, setWaView] = useState(false);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const insightPageRef = useRef(null);
  const [cookiePending, setCookiePending] = useState(() => {
    try { return !localStorage.getItem('apisai_cookie_consent'); } catch { return false; }
  });
  const inputRef = useRef(null);
  const closedPageRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };
  const { page, isWizard } = pathInfo(location.pathname);

  const fetchCoach = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/assistant/coach?page=${page}`, { headers });
      setCoach(res.data);
      // Proaktywność KONTEKSTOWA: auto-odsłoń kartę na ekranach przeglądowych,
      // gdzie jest realny krok do zrobienia. NIE przeszkadzaj w kreatorach (formularze).
      const hasAction = (res.data?.completion ?? 100) < 100 || (res.data?.new_leads ?? 0) > 0;
      if (!isWizard && hasAction && res.data?.profile_chosen && closedPageRef.current !== page) {
        setTimeout(() => setOpen(true), 1000);
      }
    } catch { /* cicho */ }
  }, [page, isWizard, token]); // eslint-disable-line

  useEffect(() => { fetchCoach(); }, [fetchCoach]);

  // Reset karty przy zmianie ekranu — świeże, kontekstowe auto-open na każdej nowej stronie.
  useEffect(() => { setOpen(false); setWaView(false); setInsight(null); }, [page]);

  // Warstwa AI: przy otwarciu karty pobierz "myślącą" podpowiedź (raz na ekran).
  useEffect(() => {
    if (!open || !token) return;
    if (insightPageRef.current === page) return;
    let cancelled = false;
    setInsightLoading(true);
    axios.get(`${API}/assistant/coach/insight?page=${page}`, { headers })
      .then((res) => { if (!cancelled) { setInsight(res.data?.insight || null); insightPageRef.current = page; } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setInsightLoading(false); });
    return () => { cancelled = true; };
  }, [open, page, token]); // eslint-disable-line

  useEffect(() => { if (chatOpen && inputRef.current) inputRef.current.focus(); }, [chatOpen]);

  // Cookie banner (z-[100], fixed bottom) — gdy aktywny, unieś asystenta ponad niego.
  useEffect(() => {
    const onConsent = () => setCookiePending(false);
    window.addEventListener('apisai-cookie-consent', onConsent);
    return () => window.removeEventListener('apisai-cookie-consent', onConsent);
  }, []);

  const handleAsk = async (confirmAction = null, quickTool = null) => {
    const q = question.trim();
    if ((!confirmAction && !quickTool && !q) || asking) return;
    setAsking(true);
    if (!confirmAction) setAgentResp(null);
    try {
      let payload;
      if (confirmAction) payload = { message: '', page, confirm: confirmAction };
      else if (quickTool) payload = { message: '', page, tool: quickTool };
      else payload = { message: q, page, session_id: null };
      const res = await axios.post(`${API}/assistant/agent`, payload, { headers });
      setAgentResp(res.data || { reply: 'Nie mogę teraz odpowiedzieć.' });
      if (!confirmAction && !quickTool) setQuestion('');
    } catch {
      setAgentResp({ reply: 'Coś poszło nie tak. Spróbuj ponownie.' });
    } finally { setAsking(false); }
  };

  if (!token || !coach) return null;
  // Asystent aktywuje się dopiero po wyborze profilu (hodowca/biznes).
  // Dla świeżych userów (domyślny 'breeder') onboardingiem zajmuje się modal powitalny.
  if (!coach.profile_chosen) return null;

  const step = coach.next_step;
  const StepIcon = ICONS[step?.icon] || Sparkles;
  const isDone = coach.completion >= 100 && coach.new_leads === 0;

  return (
    <div className={`fixed ${cookiePending ? 'bottom-28' : 'bottom-5'} right-4 sm:right-6 z-[110] flex flex-col items-end gap-3 transition-all duration-300`} data-testid="apis-assistant">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="w-[330px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white shadow-[0_18px_60px_rgba(15,23,41,0.22)] border border-[var(--apisai-border)] overflow-hidden"
            data-testid="apis-assistant-card"
          >
            {/* Header z pierścieniem postępu */}
            <div className="px-4 py-3.5 flex items-center gap-3" style={{ background: 'linear-gradient(135deg,#F97316 0%,#EA670C 100%)' }}>
              <Ring value={coach.completion} />
              <div className="flex-1 min-w-0">
                <p className="text-white text-[13px] font-bold leading-tight">Asystent APIS</p>
                <p className="text-white/80 text-[11px] leading-tight" data-testid="apis-assistant-completion">
                  {isDone ? 'Profil gotowy' : `Profil gotowy w ${coach.completion}%`}
                </p>
              </div>
              <button onClick={() => { setOpen(false); setWaView(false); closedPageRef.current = page; }} data-testid="apis-assistant-close"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3.5">
              {waView ? (
                <WaPanel onBack={() => setWaView(false)} headers={headers} />
              ) : (
              <>
              <p className="text-[13px] text-[var(--apisai-text-primary)] leading-relaxed" data-testid="apis-assistant-greeting">
                {insightLoading ? (
                  <span className="inline-flex items-center gap-2 text-[var(--apisai-text-muted)]">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--apisai-primary)]" /> Myślę…
                  </span>
                ) : (insight || coach.greeting)}
              </p>

              {/* Proaktywny push: nieodebrane zapytania — odpowiedz teraz */}
              {coach.new_leads > 0 && (
                <button
                  onClick={() => { setOpen(false); navigate('/panel/leady'); }}
                  data-testid="apis-assistant-unanswered-leads"
                  className="w-full rounded-xl border border-[var(--apisai-primary)]/25 bg-[var(--apisai-primary)]/[0.06] p-3 flex items-center gap-2.5 text-left hover:bg-[var(--apisai-primary)]/[0.1] transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-[var(--apisai-primary)]/15 flex items-center justify-center flex-shrink-0">
                    <Inbox className="w-4 h-4 text-[var(--apisai-primary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold text-[var(--apisai-text-primary)] leading-snug">
                      Masz {coach.new_leads} {leadWord(coach.new_leads)} — odpowiedz teraz
                    </p>
                    <p className="text-[11px] text-[var(--apisai-text-secondary)]">Szybka odpowiedź zwiększa szansę na klienta.</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[var(--apisai-primary)] flex-shrink-0" />
                </button>
              )}

              {/* Następny krok */}
              {step && (
                <div className="rounded-xl border border-[var(--apisai-primary)]/15 bg-[var(--apisai-primary)]/[0.05] p-3.5" data-testid="apis-assistant-next-step">
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(249,115,22,0.12)' }}>
                      <StepIcon className="w-4 h-4 text-[var(--apisai-primary)]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--apisai-primary)]">Następny krok</p>
                      <p className="text-[13px] font-bold text-[var(--apisai-text-primary)] leading-snug mt-0.5">{step.title}</p>
                      <p className="text-[12px] text-[var(--apisai-text-secondary)] leading-snug mt-1">{step.why}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setOpen(false); navigate(step.cta_route); }}
                    data-testid="apis-assistant-cta"
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg bg-[var(--apisai-primary)] text-white text-[13px] font-semibold hover:brightness-95 transition-all">
                    {step.cta_label} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Checklista (kompaktowa) */}
              {coach.checklist?.length > 0 && (
                <div className="space-y-1.5" data-testid="apis-assistant-checklist">
                  {coach.checklist.map((c) => (
                    <div key={c.id} className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${c.done ? 'bg-emerald-500' : 'bg-zinc-200'}`}>
                        {c.done && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className={`text-[12px] ${c.done ? 'text-[var(--apisai-text-muted)] line-through' : 'text-[var(--apisai-text-secondary)]'}`}>{c.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Czat — dopytaj o cokolwiek */}
              <div className="pt-1">
                {!chatOpen ? (
                  <button onClick={() => setChatOpen(true)} data-testid="apis-assistant-open-chat"
                    className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-[var(--apisai-border)] text-[var(--apisai-text-secondary)] text-[12px] font-semibold hover:bg-[var(--apisai-bg-subtle)] transition-colors">
                    <MessageCircle className="w-3.5 h-3.5" /> Zapytaj o cokolwiek
                  </button>
                ) : (
                  <div className="space-y-2">
                    {agentResp && (
                      <div className="rounded-lg bg-[var(--apisai-bg-subtle)] p-2.5 space-y-2 max-h-[200px] overflow-y-auto" data-testid="apis-assistant-agent-resp">
                        <p className="text-[12px] text-[var(--apisai-text-primary)] leading-relaxed whitespace-pre-line" data-testid="apis-assistant-reply">{agentResp.reply}</p>
                        {agentResp.action?.type === 'navigate' && (
                          <button onClick={() => { setOpen(false); navigate(agentResp.action.route); }} data-testid="apis-assistant-action-nav"
                            className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-lg bg-[var(--apisai-primary)] text-white text-[12px] font-semibold hover:brightness-95 transition-all">
                            {agentResp.action.label} <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {agentResp.action?.type === 'copy' && (
                          <button onClick={() => { try { navigator.clipboard.writeText(agentResp.action.value); toast.success('Skopiowano link'); } catch { /* noop */ } }} data-testid="apis-assistant-action-copy"
                            className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-lg border border-[var(--apisai-border)] text-[var(--apisai-text-secondary)] text-[12px] font-semibold hover:bg-white transition-colors">
                            {agentResp.action.label}
                          </button>
                        )}
                        {agentResp.action?.type === 'open_wa_panel' && (
                          <button onClick={() => setWaView(true)} data-testid="apis-assistant-action-wa"
                            className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-lg border border-emerald-200 text-emerald-700 text-[12px] font-semibold hover:bg-emerald-50 transition-colors">
                            <Bell className="w-3.5 h-3.5" /> {agentResp.action.label || 'Ustaw przypomnienia'}
                          </button>
                        )}
                        {agentResp.confirm && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleAsk(agentResp.confirm)} disabled={asking} data-testid="apis-assistant-confirm-yes"
                              className="flex-1 h-8 rounded-lg bg-emerald-600 text-white text-[12px] font-semibold hover:brightness-95 disabled:opacity-50 transition-all">
                              {agentResp.confirm.label}
                            </button>
                            <button onClick={() => setAgentResp(null)} data-testid="apis-assistant-confirm-no"
                              className="h-8 px-3 rounded-lg border border-[var(--apisai-border)] text-[var(--apisai-text-secondary)] text-[12px] font-semibold hover:bg-white transition-colors">
                              Anuluj
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {asking && (
                      <div className="flex items-center gap-2 text-[12px] text-[var(--apisai-text-muted)]">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--apisai-primary)]" /> Myślę…
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1.5" data-testid="apis-assistant-quick-actions">
                      {[
                        { tool: 'get_new_leads', label: coach.new_leads > 0 ? `Zapytania (${coach.new_leads})` : 'Zapytania' },
                        { tool: 'get_missing_steps', label: 'Czego brakuje?' },
                        { tool: 'get_my_link', label: 'Pokaż link' },
                        { tool: 'send_link_whatsapp', label: 'Wyślij na WhatsApp' },
                      ].map((qa) => (
                        <button key={qa.tool} onClick={() => handleAsk(null, qa.tool)} disabled={asking}
                          data-testid={`apis-assistant-quick-${qa.tool}`}
                          className="text-[11px] px-2.5 h-7 rounded-full border border-[var(--apisai-border)] text-[var(--apisai-text-secondary)] hover:bg-[var(--apisai-bg-subtle)] hover:border-[var(--apisai-primary)]/40 disabled:opacity-40 transition-colors">
                          {qa.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <input ref={inputRef} value={question} onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAsk(); }}
                        placeholder="Napisz lub poproś o akcję…"
                        className="flex-1 text-[12px] px-3 h-9 rounded-lg border border-[var(--apisai-border)] focus:border-[var(--apisai-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--apisai-primary)]/20 bg-[var(--apisai-bg-subtle)]"
                        data-testid="apis-assistant-chat-input" />
                      <button onClick={() => handleAsk()} disabled={!question.trim() || asking} data-testid="apis-assistant-chat-send"
                        className="w-9 h-9 rounded-lg bg-[var(--apisai-primary)] text-white flex items-center justify-center disabled:opacity-40 hover:brightness-95 transition-all">
                        <Send className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Zgoda na przypomnienia WhatsApp (RODO, w panelu) */}
              <button onClick={() => setWaView(true)} data-testid="apis-assistant-wa-open"
                className="w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-emerald-200 text-emerald-700 text-[12px] font-semibold hover:bg-emerald-50 transition-colors">
                <Bell className="w-3.5 h-3.5" /> Przypomnienia na WhatsApp
              </button>
              </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB — pływający przycisk */}
      <motion.button
        onClick={() => setOpen((o) => { if (o) closedPageRef.current = page; return !o; })}
        data-testid="apis-assistant-fab"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-[0_10px_30px_rgba(249,115,22,0.45)]"
        style={{ background: 'linear-gradient(135deg,#F97316 0%,#EA670C 100%)' }}
        aria-label="Asystent APIS"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <Sparkles className="w-6 h-6 text-white" />}
        {!open && !isDone && (
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-white ring-2 ring-[#F97316]">
            <span className="absolute inset-0.5 rounded-full bg-[#F97316] animate-pulse" />
          </span>
        )}
      </motion.button>
    </div>
  );
}
